// src/pages/AllProductsPage.js
import React, { useContext, useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { PRODUCT_CATEGORIES } from "../data/products";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getPublicCategories } from "../services/categories.api";
import { useSeo } from "../hooks/useSeo";

function AllProductsPage() {
  useSeo("products");
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isIOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

const isAndroid =
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent);

const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const videoRef = useRef(null);
const [canAutoPlay, setCanAutoPlay] = useState(false);

  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [allVariantProducts, setAllVariantProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sizeFilter, setSizeFilter] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);

  const [heelFilter, setHeelFilter] = useState("all");

  const PAGE_SIZE = isIOS || isSafari
  ? 14
  : isAndroid
  ? 20
  : 50;

const [currentPage, setCurrentPage] = useState(1);


 // price state FIRST
const [priceMin, setPriceMin] = useState(0);
const [priceMax, setPriceMax] = useState(5000);

// derived max price
const maxProductPrice = useMemo(() => {
  return products.reduce((max, p) => {
    const finalPrice = p.discount
      ? Math.round(p.price * (1 - p.discount / 100))
      : p.price;
    return finalPrice > max ? finalPrice : max;
  }, 0);
}, [products]);

useEffect(() => {
  const timer = setTimeout(() => {
    setCanAutoPlay(true);

    if (videoRef.current) {
      const playPromise = videoRef.current.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Safari / iOS may block autoplay → controls stay visible
        });
      }
    }
  }, 10000); // 10 seconds

  return () => clearTimeout(timer);
}, []);

// initialize priceMax ONCE
React.useEffect(() => {
  if (priceMax === null && maxProductPrice > 0) {
    setPriceMax(maxProductPrice);
  }
}, [maxProductPrice, priceMax]);



  // ui
  const [sortOption, setSortOption] = useState("bestSelling");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [gridMode, setGridMode] = useState("two");

  // whatsapp + scroll
  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const whatsappMessage =
    lang === "EN"
      ? "Hi, I need help browsing all products in your store."
      : "مرحباً، أحتاج مساعدة في تصفح كل المنتجات.";

  const whatsappLink = `https://wa.me/96179338100?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  // load products
  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    getAllProducts({})
      .then((data) => {
        if (mounted) {
          const mapped = data.map(mapBackendProduct);

// 🚑 Mobile safety guard (prevents Chrome tab crash)
const safeProducts = isIOS
  ? mapped.slice(0, 60)
  : isMobile
  ? mapped.slice(0, 120)
  : mapped;


setProducts(safeProducts);
setAllVariantProducts(safeProducts);

        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => (mounted = false);
  }, []);

  React.useEffect(() => {
    getPublicCategories().then(setAllCategories).catch(console.error);
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => setShowWhatsappCTA(true), 1000);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
  if (isMobile) return; // 🚑 MOBILE SAFETY: prevent Chrome tab crash

  let ticking = false;

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 300);
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);
  return () => window.removeEventListener("scroll", onScroll);
}, [isMobile]);



  const TEXT = {
    EN: {
      title: "All Products",
      filters: "Filters",
      sortBy: "Sort by",
      bestSelling: "Best Selling",
      priceLowHigh: "Price: Low to High",
      priceHighLow: "Price: High to Low",
      productsWord: "products",
      category: "Category",
      size: "Size",
      color: "Color",
      price: "Price",
      heelHeight: "Heel height",
      all: "All",
      low: "Low",
      mid: "Mid",
      high: "High",
      appliedFilters: "Applied filters",
      clear: "Clear",
      clearAll: "Clear all",
      apply: "Apply",
      min: "Min",
      max: "Max",
      noProducts: "No products found.",
    },
    AR: {
      title: "كل المنتجات",
      filters: "تصفية",
      sortBy: "ترتيب حسب",
      bestSelling: "الأكثر مبيعاً",
      priceLowHigh: "السعر: من الأقل للأعلى",
      priceHighLow: "السعر: من الأعلى للأقل",
      productsWord: "منتج",
      category: "الفئة",
      size: "المقاس",
      color: "اللون",
      price: "السعر",
      heelHeight: "ارتفاع الكعب",
      all: "الكل",
      low: "منخفض",
      mid: "متوسط",
      high: "عالٍ",
      appliedFilters: "الفلاتر المفعّلة",
      clear: "مسح",
      clearAll: "مسح الكل",
      apply: "تطبيق",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      noProducts: "لا توجد منتجات.",
    },
  }[lang];

  const CATEGORY_OPTIONS = allCategories.map((c) => ({
    key: c.slug,
    labelEN: c.name_en,
    labelAR: c.name_ar,
  }));

  const allSizes = useMemo(() => {
  if (!isFilterOpen) return [];
  const set = new Set();
  allVariantProducts.forEach((p) =>
    p.sizes?.forEach(({ size }) => {
      if (size !== null && size !== undefined && String(size).trim() !== "")
        set.add(size);
    })
  );
  return Array.from(set).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb))
      return String(a).localeCompare(String(b));
    return na - nb;
  });
}, [allVariantProducts, isFilterOpen]);

const allColors = useMemo(() => {
  if (!isFilterOpen) return [];
  const c = [];
  allVariantProducts.forEach((p) =>
    p.colors?.forEach((col) => {
      if (!c.find((x) => x.name === col.name)) c.push(col);
    })
  );
  return c;
}, [allVariantProducts, isFilterOpen]);

const filterState = useMemo(
  () => ({
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  }),
  [
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  ]
);


  const filteredAndSorted = useMemo(() => {
  let result = [...products];

  const {
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  } = filterState;

  if (categoryFilter.length) {
    result = result.filter((p) =>
      p.categories?.some((c) => categoryFilter.includes(c))
    );
  }

  if (sizeFilter.length) {
    result = result.filter((p) =>
      p.sizes?.some(
        (s) => sizeFilter.includes(s.size) && s.inStock
      )
    );
  }

  if (colorFilter.length) {
    result = result.filter((p) =>
      p.colors?.some((c) => colorFilter.includes(c.name))
    );
  }

  if (heelFilter !== "all") {
    result = result.filter((p) => {
      if (p.heelHeight == null) return false;
      if (heelFilter === "low") return p.heelHeight < 6;
      if (heelFilter === "mid") return p.heelHeight >= 6 && p.heelHeight <= 8;
      if (heelFilter === "high") return p.heelHeight > 8;
      return true;
    });
  }

  if (priceMax !== null) {
    result = result.filter((p) => {
      const finalPrice = p.discount
        ? Math.round(p.price * (1 - p.discount / 100))
        : p.price;
      return finalPrice >= priceMin && finalPrice <= priceMax;
    });
  }

  if (sortOption === "priceLowHigh") {
    result.sort((a, b) => a.price - b.price);
  } else if (sortOption === "priceHighLow") {
    result.sort((a, b) => b.price - a.price);
  }

  return result;
}, [products, filterState]);


const totalPages = Math.max(
  1,
  Math.ceil(filteredAndSorted.length / PAGE_SIZE)
);

const paginatedProducts = useMemo(() => {
  const start = (currentPage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  return filteredAndSorted.slice(start, end);
}, [filteredAndSorted, currentPage, PAGE_SIZE]);


React.useEffect(() => {
  setCurrentPage(1);
}, [filterState]);


  const productCount = filteredAndSorted.length;
  const gridClasses =
    gridMode === "two"
      ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";


  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center">
        <p className="text-sm text-[#7A7674]">
          {lang === "EN" ? "Loading products…" : "جاري تحميل المنتجات…"}
        </p>
      </div>
    );
  }

  const toggleCategory = (key) => {
  setCategoryFilter((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
};

const clearAllFilters = () => {
  setCategoryFilter([]);
  setSizeFilter([]);
  setColorFilter([]);
  setHeelFilter("all");
  setPriceMin(0);
  setPriceMax(maxProductPrice || 400);
};


// same behavior as ProductsPage
const showHeelFilter = true;

// Applied filters (chips)
const appliedFilters = [];

if (categoryFilter.length) {
  const labels = CATEGORY_OPTIONS.filter((c) =>
    categoryFilter.includes(c.key)
  )
    .map((c) => (lang === "EN" ? c.labelEN : c.labelAR))
    .join(", ");

  appliedFilters.push({
  id: "category",
  label: `${TEXT.category}: ${labels}`,
  onClear: () => setCategoryFilter([]),
});
}

if (sizeFilter.length) {
  appliedFilters.push({
    id: "size",
    label: `${TEXT.size}: ${sizeFilter.join(", ")}`,
    onClear: () => setSizeFilter([]),
  });
}

if (colorFilter.length) {
  appliedFilters.push({
    id: "color",
    label: `${TEXT.color}: ${colorFilter.join(", ")}`,
    onClear: () => setColorFilter([]),
  });
}


if (heelFilter !== "all") {
  const heelLabel =
    heelFilter === "low"
      ? TEXT.low
      : heelFilter === "mid"
      ? TEXT.mid
      : TEXT.high;

  appliedFilters.push({
    id: "heel",
    label: `${TEXT.heelHeight}: ${heelLabel}`,
    onClear: () => setHeelFilter("all"),
  });
}

if (priceMin > 0 || priceMax < maxProductPrice) {
  appliedFilters.push({
    id: "price",
    label: `${TEXT.price}: $${priceMin} – $${priceMax}`,
    onClear: () => {
      setPriceMin(0);
      setPriceMax(maxProductPrice || 400);
    },
  });
}


  return (
    <div className="w-full bg-white min-h-screen flex flex-col overflow-x-hidden">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <div
        className="w-full min-h-screen bg-white pt-[95px] px-4 pb-24"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        <button
          onClick={() => navigate(-1)}
          className="absolute top-[110px] text-3xl opacity-60 hover:opacity-100"
          style={lang === "AR" ? { right: 16 } : { left: 16 }}
        >
          ×
        </button>

        <h1 className="text-xl font-semibold text-center tracking-wide text-[#2E3135] mt-2">
          {TEXT.title.toUpperCase()}
        </h1>
        <div className="h-[2px] w-12 bg-black mx-auto mt-2 mb-4"></div>
        
        {/* FILTERS + SORT BAR */}
<div className="flex gap-2 mb-3">
  {/* FILTER BUTTON */}
  <button
    onClick={() => setIsFilterOpen(true)}
    className="
      flex-1 flex items-center justify-center gap-2
      border border-[#E6D8D2] rounded-md
      py-2.5 text-xs font-semibold tracking-wide
      bg-white active:scale-[0.98]
    "
  >
    <span>{TEXT.filters.toUpperCase()}</span>
    <span className="flex flex-col gap-[2px]">
      <span className="w-3 h-[1.5px] bg-black rounded-full" />
      <span className="w-4 h-[1.5px] bg-black rounded-full" />
      <span className="w-2 h-[1.5px] bg-black rounded-full" />
    </span>
  </button>

  {/* SORT BUTTON */}
  <div className="relative flex-1">
    <button
      onClick={() => setIsSortOpen((p) => !p)}
      className="
        w-full flex items-center justify-center gap-2
        border border-[#E6D8D2] rounded-md
        py-2.5 text-xs font-semibold tracking-wide
        bg-white active:scale-[0.98]
      "
    >
      <span>{TEXT.sortBy.toUpperCase()}</span>
      <span className="text-[10px]">{isSortOpen ? "▲" : "▼"}</span>
    </button>

    {isSortOpen && (
      <div
        className="
          absolute right-0 mt-1 w-44
          bg-white border border-[#E6D8D2]
          rounded-md shadow-lg z-20 text-xs
        "
        style={lang === "AR" ? { left: 0, right: "auto" } : {}}
      >
        <button
          onClick={() => {
            setSortOption("bestSelling");
            setIsSortOpen(false);
          }}
          className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
            sortOption === "bestSelling" ? "font-semibold" : ""
          }`}
        >
          {TEXT.bestSelling}
        </button>
        <button
          onClick={() => {
            setSortOption("priceLowHigh");
            setIsSortOpen(false);
          }}
          className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
            sortOption === "priceLowHigh" ? "font-semibold" : ""
          }`}
        >
          {TEXT.priceLowHigh}
        </button>
        <button
          onClick={() => {
            setSortOption("priceHighLow");
            setIsSortOpen(false);
          }}
          className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
            sortOption === "priceHighLow" ? "font-semibold" : ""
          }`}
        >
          {TEXT.priceHighLow}
        </button>
      </div>
    )}
  </div>
</div>

{/* COUNT + GRID TOGGLE */}
<div className="flex items-center justify-between mb-4">
  <span className="text-[11px] text-[#7A7674]">
    {productCount} {TEXT.productsWord}
  </span>

  <div className="flex items-center gap-1">
    <button
      onClick={() => setGridMode("two")}
      className={`
        w-7 h-7 border rounded-md flex items-center justify-center
        ${gridMode === "two" ? "bg-black text-white" : "border-[#E6D8D2]"}
      `}
    >
      <div className="grid grid-cols-2 grid-rows-2 gap-[2px] w-3 h-3">
        <span className="bg-current" />
        <span className="bg-current" />
        <span className="bg-current" />
        <span className="bg-current" />
      </div>
    </button>

    <button
      onClick={() => setGridMode("one")}
      className={`
        w-7 h-7 border rounded-md flex items-center justify-center
        ${gridMode === "one" ? "bg-black text-white" : "border-[#E6D8D2]"}
      `}
    >
      <div className="flex flex-col gap-[2px] w-3">
        <span className="h-[3px] bg-current rounded-sm" />
        <span className="h-[3px] bg-current rounded-sm" />
        <span className="h-[3px] bg-current rounded-sm" />
      </div>
    </button>
  </div>
</div>

<div className={`grid ${gridClasses} gap-3 md:gap-4`}>
  {paginatedProducts.map((p) => (
  <ProductCard key={p.id} product={p} />
))}
</div>

{/* PAGINATION */}
{totalPages > 1 && (
  <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
    {/* PREV */}
    <button
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
      className={`
        px-3 py-1 border rounded-md text-xs font-semibold
        ${
          currentPage === 1
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-black hover:text-white"
        }
      `}
    >
      {lang === "EN" ? "Prev" : "السابق"}
    </button>

    {/* PAGE NUMBERS */}
    {Array.from({ length: totalPages }).map((_, i) => {
      const page = i + 1;
      return (
        <button
          key={page}
          onClick={() => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className={`
            w-8 h-8 rounded-md border text-xs font-semibold
            ${
              currentPage === page
                ? "bg-black text-white border-black"
                : "border-[#E6D8D2] hover:bg-black hover:text-white"
            }
          `}
        >
          {page}
        </button>
      );
    })}

    {/* NEXT */}
    <button
      disabled={currentPage === totalPages}
      onClick={() =>
        setCurrentPage((p) => Math.min(totalPages, p + 1))
      }
      className={`
        px-3 py-1 border rounded-md text-xs font-semibold
        ${
          currentPage === totalPages
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-black hover:text-white"
        }
      `}
    >
      {lang === "EN" ? "Next" : "التالي"}
    </button>
  </div>
)}


        {/* ================= INSTAGRAM VIDEO (UNCHANGED) ================= */}
        <div className="w-full px-4 mt-10 mb-20">
          <h2 className="text-lg font-semibold tracking-wide text-center text-[#2E3135] mb-3">
            {lang === "EN" ? "Inside Our Store" : "داخل متجرنا"}
          </h2>
         <video
  ref={videoRef}
  src="/videos/our-store.mp4"
  className="w-full max-w-sm mx-auto rounded-2xl shadow-lg"
  muted={!isIOS}          // muted for autoplay safety
  loop={!isIOS}
  controls={isIOS}       // iOS needs controls
  playsInline
  preload="metadata"
/>

        </div>
      </div>

     {/* FILTER DRAWER (LEFT) */}
{isFilterOpen && (
<div
  className={`
    fixed inset-0 z-[9999]
    transition-opacity duration-300
    ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
  `}
>
  {/* Backdrop */}
  <div
    className="absolute inset-0 bg-black/40"
    onClick={() => setIsFilterOpen(false)}
  />

  {/* Panel */}
  <div
    className={`
      absolute top-0 bottom-0 left-0
      w-[90%] max-w-sm bg-white
      shadow-2xl
      transform transition-transform duration-300
      ${isFilterOpen ? "translate-x-0" : "-translate-x-full"}
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    {/* HEADER */}
    <div className="flex items-center justify-between px-4 py-4 border-b border-[#E6D8D2]">
      <span className="text-sm font-semibold">{TEXT.filters}</span>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="text-2xl opacity-60 hover:opacity-100"
      >
        ×
      </button>
    </div>

    {/* CONTENT */}
    <div className="h-[calc(100%-110px)] overflow-y-auto px-4 pt-3 pb-24 text-sm">

      {/* Applied Filters */}
      {appliedFilters.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#2E3135]">
              {TEXT.appliedFilters}
            </span>
            <button
              onClick={clearAllFilters}
              className="text-[11px] text-[#7A7674] underline"
            >
              {TEXT.clearAll}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {appliedFilters.map((f) => (
              <button
                key={f.id}
                onClick={f.onClear}
                disabled={!f.onClear}
                className={`
                  px-2 py-[3px] rounded-full bg-[#F1E7E2]
                  text-[11px] text-[#2E3135]
                  ${!f.onClear ? "opacity-60 cursor-not-allowed" : ""}
                `}
              >
                {f.label} ×
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORY */}
      <DrawerSection title={TEXT.category}>
        <div className="space-y-2 mt-2">
          {CATEGORY_OPTIONS.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={categoryFilter.includes(c.key)}
                onChange={() => toggleCategory(c.key)}
                className="w-4 h-4 border-[#C9B7AE] rounded-sm"
              />
              <span>{lang === "EN" ? c.labelEN : c.labelAR}</span>
            </label>
          ))}
        </div>
      </DrawerSection>

      {/* SIZE */}
      <DrawerSection title={TEXT.size}>
        <div className="flex flex-wrap gap-2 mt-2">
          {allSizes.map((s) => (
            <label key={String(s)} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sizeFilter.includes(s)}
onChange={() =>
  setSizeFilter((prev) =>
    prev.includes(s)
      ? prev.filter((x) => x !== s)
      : [...prev, s]
  )
}

                className="w-4 h-4 border-[#C9B7AE] rounded-sm"
              />
              <span>{String(s)}</span>
            </label>
          ))}
        </div>
      </DrawerSection>

      {/* COLOR */}
      <DrawerSection title={TEXT.color}>
        <div className="flex flex-wrap gap-3 mt-2">
          {allColors.map((c) => (
            <button
              key={c.name}
              onClick={() =>
  setColorFilter((prev) =>
    prev.includes(c.name)
      ? prev.filter((x) => x !== c.name)
      : [...prev, c.name]
  )
}
              className="flex flex-col items-center gap-1 text-[10px]"
            >
              <span
                className={`
                  w-7 h-7 rounded-full border
                  ${colorFilter === c.name ? "border-black" : "border-[#D3C5BE]"}
                `}
                style={{ backgroundColor: c.code }}
              />
              <span className="text-[#3F4247]">{c.name}</span>
            </button>
          ))}
        </div>
      </DrawerSection>

      {/* HEEL */}
      {showHeelFilter && (
        <DrawerSection title={TEXT.heelHeight}>
          <div className="flex flex-wrap gap-2 mt-2">
            {["all", "low", "mid", "high"].map((h) => (
              <button
                key={h}
                onClick={() => setHeelFilter(h)}
                className={`
                  px-3 py-1 rounded-full border text-xs
                  ${
                    heelFilter === h
                      ? "bg-black text-white border-black"
                      : "border-[#E6D8D2] text-[#2E3135]"
                  }
                `}
              >
                {TEXT[h]}
              </button>
            ))}
          </div>
        </DrawerSection>
      )}

      {/* PRICE */}
      <DrawerSection title={TEXT.price}>
        <div className="mt-3 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <p className="text-[11px] text-[#7A7674] mb-1">{TEXT.min}</p>
              <input
                type="range"
                min={0}
                max={maxProductPrice}
                value={priceMin}
                onChange={(e) =>
                  setPriceMin(Math.min(Number(e.target.value), priceMax))
                }
                className="w-full accent-black"
              />
            </div>
            <div className="flex-1">
              <p className="text-[11px] text-[#7A7674] mb-1">{TEXT.max}</p>
              <input
                type="range"
                min={0}
                max={maxProductPrice}
                value={priceMax}
                onChange={(e) =>
                  setPriceMax(Math.max(Number(e.target.value), priceMin))
                }
                className="w-full accent-black"
              />
            </div>
          </div>
        </div> 
      </DrawerSection>
    </div>


    {/* STICKY BOTTOM ACTIONS */}
    <div className="absolute bottom-0 left-0 right-0 border-t border-[#E6D8D2] bg-white px-4 py-3 flex gap-3">
      <button
        onClick={clearAllFilters}
        className="flex-1 py-2 border border-black rounded-md text-xs font-semibold"
      >
        {TEXT.clear.toUpperCase()}
      </button>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="flex-1 py-2 rounded-md bg-black text-white text-xs font-semibold"
      >
        {TEXT.apply.toUpperCase()}
      </button>
    </div>
  </div>
</div>
)}


      {/* WhatsApp + Scroll */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed z-[9999] top-24 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-black text-white"
        >
          ↑
        </button>
      )}

      {showWhatsappCTA && (
  <div
    className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2"
    dir="ltr"
  >
    {/* Close button ABOVE */}
    <button
      onClick={() => setShowWhatsappCTA(false)}
      className="w-7 h-7 rounded-full bg-white border border-[#E6D8D2] flex items-center justify-center text-black text-sm shadow-md opacity-90 hover:opacity-100"
      aria-label="Close WhatsApp"
      title="Close"
    >
      ×
    </button>

    {/* WhatsApp Button */}
    <a
      href={whatsappLink}
      target="_blank"
      rel="noreferrer"
      className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition"
      style={{ backgroundColor: "#25D366" }}
      aria-label="WhatsApp"
      title="WhatsApp"
    >
      <img
        src="/icons/whatsapp.png"
        alt="WhatsApp"
        className="w-7 h-7 object-contain"
      />
    </a>
  </div>
)}
 <Footer className="mt-auto" />
    </div>
  );
}

/**
 * Small helper component for collapsible sections inside the filter drawer.
 * (Same as ProductsPage – kept identical on purpose)
 */
function DrawerSection({ title, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#F1E7E2] py-3">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between text-sm font-semibold"
      >
        <span>{title}</span>
        <span className="text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}


export default AllProductsPage;
